import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FileObject } from 'src/interfaces/shared';
import { deleteUtilityAttachmentById, downloadAttachment } from 'src/services/utility';
import styles from './FileUpload.module.scss';
import Icon from '../Icon/Icon';
import Button, { ButtonToneProps } from '../Button/Button';

export interface FileUploadProps {
  onChange?: (files: FileObject[]) => void;
  disabled?: boolean;
  defaultFiles?: FileObject[];
  supportedTypes?: string[];
  downloadMode?: boolean;
  hideDeleteButton?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onChange,
  disabled,
  defaultFiles,
  supportedTypes,
  downloadMode,
  hideDeleteButton,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [files, setFiles] = React.useState<FileObject[]>(defaultFiles || []);

  const acceptTypes = supportedTypes?.join(',') || '.pdf, .jpg, .jpeg, .png';

  // return all new files, new means they have no id yet
  const returnNewFiles = (files: FileObject[]) => {
    const newFiles = files.filter(file => file.id === null);
    if (onChange) onChange(newFiles);
  };

  // Function to convert file to base64 string
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Custom logic to determine the type of the file
  const getFileType = (mimetype: string): string => {
    if (mimetype.includes('pdf')) {
      return 'document';
    }
    if (mimetype.includes('image')) {
      return 'image';
    }
    return 'unknown';
  };

  // Function to process each file
  const processFile = async (file: File): Promise<FileObject> => {
    const base64File = await convertToBase64(file);
    return {
      id: null,
      name: file.name,
      file: base64File,
      mimetype: file.type,
      type: getFileType(file.type), // Determine file type based on mime type or custom logic
    };
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const fileArray = Array.from(event.target.files); // Convert FileList to array
      const processedFiles = await Promise.all(fileArray.map(processFile));
      const newSetFiles = [...files, ...processedFiles];
      setFiles(newSetFiles);
      returnNewFiles(newSetFiles);
    }
  };

  const removeFileByIndex = async (index: number) => {
    const attachment = files[index];
    const idToDelete = attachment.id;
    if (idToDelete) await deleteUtilityAttachmentById(idToDelete);
    const updatedFiles = [...files.slice(0, index), ...files.slice(index + 1)];
    setFiles(updatedFiles);
    returnNewFiles(updatedFiles);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (defaultFiles) setFiles(defaultFiles);
  }, [defaultFiles]);

  return (
    <div className={classNames(styles.root)}>
      {!disabled && (
        <div className={classNames(styles.zone, 'mb-8 flex gap-8 items-center flex-wrap')}>
          <input
            style={{ display: 'none' }}
            ref={fileInputRef}
            multiple
            accept={acceptTypes}
            onChange={handleFileChange}
            id="file"
            type="file"
          />
          <Button tone={ButtonToneProps.SECONDARY} onClick={handleButtonClick} title={t('general.select_files')} />
          <span className="typo-text-14 text-faded">
            {!files || files.length === 0
              ? t('general.no_files_selected')
              : `${files.length} ${t('general.files_selected')}`}
          </span>
        </div>
      )}
      {files && files.length > 0 && (
        <div className="flex flex-col gap-8">
          {files
            .sort((a, b) => (a?.id || 0) - (b?.id || 0))
            .map((file, index) => (
              <div key={`file-${file.id || index}`} className={styles.file}>
                <span className="typo-text-14--bold text-full">
                  {file.name}
                  {!file.id && <span className="typo-text-12--bold text-faded">({t('general.new')})</span>}
                </span>

                <div className="ml-auto flex items-center">
                  {disabled && downloadMode && file.id && (
                    <button
                      aria-label={`Download ${file.name}`}
                      type="button"
                      className="text-full"
                      onClick={() => downloadAttachment(Number(file.id))}>
                      <Icon name="download" width={20} height={20} />
                    </button>
                  )}
                  {!disabled && !hideDeleteButton && (
                    <button
                      aria-label={`Delete ${file.name}`}
                      type="button"
                      className="text-full"
                      onClick={() => removeFileByIndex(index)}>
                      <Icon name="deleteCircle" width={20} height={20} />
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
